import { apiClient } from '@/services/api/apiClient';
import { ENDPOINTS } from '@/services/api/endpoints';
import { PILLAR_TASK_STATUS } from '@/features/pillarChair/data/mockPillarData';
import { approvedScoreSum, projectPercentFromTasks } from '@/utils/progress';

export function toIsoDateStart(ymd) {
    if (!ymd) return new Date().toISOString();
    return new Date(`${ymd}T00:00:00.000Z`).toISOString();
}

export function toIsoDateEnd(ymd) {
    if (!ymd) return new Date().toISOString();
    return new Date(`${ymd}T23:59:59.999Z`).toISOString();
}

function toYmd(isoOrDate) {
    if (isoOrDate == null || isoOrDate === '') return '';
    const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
}

/** Default `weight` when the caller omits it (mirrors the API). */
export const DEFAULT_WEIGHT = 50;
/** Default `targetValue` when the caller omits it (mirrors the API). */
export const DEFAULT_TARGET_VALUE = 100;

/**
 * Project / task `weight` and `targetValue`: whole numbers 1–100.
 *
 * Rejects rather than clamps, matching `normalizeOneToHundred` on the server
 * (cop-32/src/utils/boundedValue.js) — silently clamping here just meant the UI
 * and the API disagreed about what was saved.
 */
export function normalizeInt1To100(value, fieldName, fallback) {
    if (value == null || value === '') return fallback;
    const n = Number(value);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 100) {
        throw new Error(`${fieldName} must be an integer between 1 and 100`);
    }
    return n;
}

function displayNameFromUser(u) {
    if (!u) return '';
    const parts = [u.firstName, u.middleName, u.lastName].filter(Boolean);
    const s = parts.join(' ').trim();
    return s || u.email || u.id;
}

function primaryRoleLabel(u) {
    const ar = u?.account?.accountRoles;
    if (!Array.isArray(ar) || ar.length === 0) return 'Member';
    const name = ar[0]?.role?.name;
    return name || 'Member';
}

function mapTaskStatusToUi(status) {
    switch (status) {
        case 'COMPLETED':
            return PILLAR_TASK_STATUS.COMPLETED;
        case 'UNDER_REVIEW':
        case 'SUBMITTED':
            return PILLAR_TASK_STATUS.UNDER_REVIEW;
        case 'STARTED':
        case 'IN_PROGRESS':
            return PILLAR_TASK_STATUS.STARTED;
        case 'FEEDBACK':
            return PILLAR_TASK_STATUS.REJECTED;
        case 'NOT_STARTED':
        default:
            return PILLAR_TASK_STATUS.NOT_STARTED;
    }
}

/** Per submission row — independent of task-level status (allows history + reversals). */
function submissionUiStatus(inProgress) {
    if (!inProgress.reviewed) return 'pending';
    if (inProgress.status === true) return 'approved';
    return 'rejected';
}

/** Maps API `Project` (with `account.user`) to the pillar-chair projects table row shape. */
export function mapApiProjectToChairUi(p) {
    const score = p?.rolledUpScore != null && Number.isFinite(Number(p.rolledUpScore))
        ? Math.round(Number(p.rolledUpScore))
        : 0;
    const acc = p?.account;
    const u = acc?.user;
    const assignedManagerId = p?.accountId ?? acc?.id ?? null;
    let assignedManagerUser = null;
    if (u && acc) {
        assignedManagerUser = {
            id: u.id,
            firstName: u.firstName,
            middleName: u.middleName,
            lastName: u.lastName,
            email: u.email,
            account: { id: acc.id },
        };
    }
    return {
        id: p.id,
        name: p.name,
        description: p.description != null && String(p.description).trim() !== '' ? p.description : '—',
        isStar: Boolean(p?.isStar),
        weight: p?.weight != null && Number.isFinite(Number(p.weight)) ? Math.trunc(Number(p.weight)) : 50,
        targetValue: p.targetValue != null ? Math.trunc(Number(p.targetValue)) : 100,
        projectStartDate: toYmd(p?.projectStartDate),
        projectEndDate: toYmd(p?.projectEndDate),
        progressPercent: Math.min(100, Math.max(0, score)),
        assignedManagerId,
        assignedManagerUser,
        _raw: p,
    };
}

export function mapApiTaskToChairUi(task, currentUserId) {
    const assignedUsers = (task.taskAssignments || []).map((ta) => ({
        userId: ta.userId,
        name: displayNameFromUser(ta.user),
        isChair: ta.isChair,
    }));

    /** Task-level chair flag per user, so a sender's label can say "(Chair)". */
    const chairByUserId = new Map(
        (task.taskAssignments || []).map((ta) => [ta.userId, ta.isChair === true]),
    );

    const chat = (task.messages || []).map((m) => {
        const senderName = displayNameFromUser(m.sender) || 'Unknown user';
        const senderRole = primaryRoleLabel(m.sender);
        const senderIsChair = chairByUserId.get(m.senderId) === true;
        return {
            id: m.id,
            text: m.message,
            date: toYmd(m.createdAt),
            from: m.senderId === currentUserId ? 'chair' : 'member',
            senderId: m.senderId,
            senderName,
            senderRole,
            senderIsChair,
            /** "Abebe Kebede (Project Manager (Chair))" — name first, then role, chair nested. */
            senderLabel: `${senderName} (${senderRole}${senderIsChair ? ' (Chair)' : ''})`,
        };
    });

    const submissions = (task.inProgressEntries || []).map((ip) => ({
        id: ip.id,
        date: toYmd(ip.timestamp),
        status: submissionUiStatus(ip),
        evidence: (ip.evidences || []).map((e) => e.filePath || e.evidence).filter(Boolean),
        /** Member narrative from InProgress.description */
        reportMessage: ip.description != null ? String(ip.description) : '',
        reviewed: ip.reviewed === true,
        /** Latest chair note from most recent review event (if any) */
        feedback:
            Array.isArray(ip.reviewEvents) && ip.reviewEvents.length > 0
                ? ip.reviewEvents[ip.reviewEvents.length - 1]?.note || null
                : null,
        reviewHistory: (ip.reviewEvents || []).map((ev) => ({
            id: ev.id,
            createdAt: ev.createdAt,
            date: toYmd(ev.createdAt),
            approved: ev.approved === true,
            note: ev.note != null ? String(ev.note) : '',
            actorName: displayNameFromUser(ev.actor),
        })),
        progressPercent: ip.score ?? 0,
    }));

    return {
        id: task.id,
        projectId: task.projectId ?? null,
        pillarId: task.pillarId ?? null,
        locationId: task.locationId ?? null,
        locationName: task.location?.name ?? null,
        name: task.name,
        description: task.description,
        startDate: toYmd(task.startDate),
        endDate: toYmd(task.endDate),
        status: mapTaskStatusToUi(task.status),
        priority: task.priority,
        weight:
            task.weight != null && Number.isFinite(Number(task.weight))
                ? Math.trunc(Number(task.weight))
                : 50,
        isStar: Boolean(task.isStar),
        targetValue:
            task.targetValue != null && Number.isFinite(Number(task.targetValue))
                ? Number(task.targetValue)
                : 100,
        assignedUsers,
        submissions,
        chat,
        _raw: task,
    };
}

export function mapProjectRowForManagement(project, tasks) {
    const taskList = Array.isArray(tasks) ? tasks : [];
    const completedTasks = taskList.filter(
        (t) => approvedScoreSum(t) >= (Number(t?.targetValue) || 100),
    ).length;
    const totalTasks = taskList.length;
    // Weighted mean of task completion, matching `Project.rolledUpScore` on the
    // server. This was the share of tasks marked complete, which answers a
    // different question: it ignored task weight and counted a task at 99% the
    // same as one not started.
    const progress = Math.round(projectPercentFromTasks(taskList));
    return {
        id: project.id,
        name: project.name,
        description: project.description,
        startDate: toYmd(project.projectStartDate),
        endDate: toYmd(project.projectEndDate),
        dueDate: project.projectEndDate,
        targetValue: project.targetValue,
        projectId: project.id,
        projectName: project.name ?? null,
        pillarId: project.pillarId ?? null,
        tasks: taskList.map((t) => ({
            targetValue:
                t.targetValue != null && Number.isFinite(Number(t.targetValue))
                    ? Number(t.targetValue)
                    : 100,
            isStar: Boolean(t.isStar),
            approvedScoreSum: (Array.isArray(t.inProgressEntries) ? t.inProgressEntries : [])
                .filter((entry) => entry?.status === true && entry?.reviewed === true)
                .reduce((sum, entry) => sum + (Number(entry?.score) || 0), 0),
            id: t.id,
            name: t.name,
            status: mapTaskStatusToUi(t.status),
            startDate: toYmd(t.startDate),
            endDate: toYmd(t.endDate),
            reportRangeHours:
                t.reportRange != null && Number.isFinite(Number(t.reportRange))
                    ? Number(t.reportRange)
                    : 24,
            assignedCount: Array.isArray(t.taskAssignments) ? t.taskAssignments.length : 0,
            assignedUsers: Array.isArray(t.taskAssignments)
                ? t.taskAssignments.map((ta) => ({
                    userId: ta.userId,
                    name: displayNameFromUser(ta.user),
                    isChair: Boolean(ta.isChair),
                }))
                : [],
            latestReportAt:
                Array.isArray(t.inProgressEntries) && t.inProgressEntries.length > 0
                    ? toYmd(
                        [...t.inProgressEntries]
                            .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0]
                            ?.timestamp,
                    )
                    : null,
        })),
        _progress: progress,
        _totalTasks: totalTasks,
        _completedTasks: completedTasks,
    };
}

export function mapProjectForDashboard(project) {
    const due = project.projectEndDate ? new Date(project.projectEndDate) : null;
    const now = new Date();
    let status = 'in-progress';
    if (due && due < now) status = 'overdue';
    const label = project.name || project.description || project.id;
    const name = label.length > 72 ? `${label.slice(0, 72)}…` : label;
    return {
        id: project.id,
        name,
        description: project.description,
        startDate: toYmd(project.projectStartDate),
        endDate: toYmd(project.projectEndDate),
        progress: Math.min(100, Math.max(0, project.rolledUpScore ?? 0)),
        status,
    };
}

/** Backend rejects unknown keys; keep level an int and percent a number. */
export function toCheckpointPayload(payload) {
    const out = {};
    if (payload?.name !== undefined) out.name = String(payload.name ?? '').trim();
    if (payload?.level !== undefined) out.level = Math.trunc(Number(payload.level));
    if (payload?.percent !== undefined) out.percent = Number(payload.percent);
    return out;
}

export const pillarChairService = {
    async listCities() {
        const { data } = await apiClient.get(ENDPOINTS.CITIES.LIST);
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.data)) return data.data;
        return [];
    },
    async resolvePillarScope(scope) {
        if (scope?.taskForceId && scope?.pillarId) {
            return { taskForceId: scope.taskForceId, id: scope.pillarId };
        }
        return this.pillarContext();
    },
    async getMyPillar() {
        const { data } = await apiClient.get(ENDPOINTS.PILLARS.MY);
        return data;
    },

    /**
     * Returns this service pinned to an explicit pillar instead of the caller's own.
     *
     * Every scoped method here bottoms out in `getMyPillar()` (directly, or through
     * `pillarContext` / `listProjectsRaw`), so overriding just that one method on a
     * prototype-linked copy re-scopes the whole service without touching call sites.
     * Used by the admin Project Management page, which works across all pillars.
     *
     * @param {{ id: string, taskForceId: string }} pillar full pillar row
     */
    forPillar(pillar) {
        if (!pillar?.id || !pillar?.taskForceId) return this;
        return Object.create(this, {
            getMyPillar: { value: async () => ({ data: pillar }) },
        });
    },

    /** Single round-trip style load for the pillar chair home dashboard. */
    async loadPillarChairDashboard() {
        const body = await this.getMyPillar();
        const pillar = body?.data;
        if (!pillar?.taskForceId || !pillar?.id) {
            return { pillar: pillar ?? null, projects: [] };
        }
        // A project-fetch failure must not erase an already-resolved pillar
        // (Promise.allSettled callers treat this whole promise as rejected otherwise,
        // which incorrectly shows "No pillar assigned" for an account that has one).
        try {
            const { data } = await apiClient.get(
                ENDPOINTS.HIERARCHY.PROJECTS.LIST(pillar.taskForceId, pillar.id),
            );
            const raw = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            return { pillar, projects: raw.map(mapProjectForDashboard) };
        } catch {
            return { pillar, projects: [] };
        }
    },

    async listProjectsRaw() {
        const body = await this.getMyPillar();
        const pillar = body?.data;
        if (!pillar?.taskForceId || !pillar?.id) {
            return { pillar: null, projects: [] };
        }
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.PROJECTS.LIST(pillar.taskForceId, pillar.id),
        );
        const projects = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        return { pillar, projects };
    },

    async listProjectsForDashboard() {
        const { pillar, projects } = await this.listProjectsRaw();
        if (!pillar) return { pillar: null, projects: [] };
        return { pillar, projects: projects.map(mapProjectForDashboard) };
    },

    async listProjectsForManagement() {
        const { pillar, projects } = await this.listProjectsRaw();
        if (!pillar) return [];
        const rows = await Promise.all(
            projects.map(async (proj) => {
                const { data: tasks } = await apiClient.get(
                    ENDPOINTS.HIERARCHY.TASKS.LIST(pillar.taskForceId, pillar.id, proj.id),
                );
                const taskList = Array.isArray(tasks) ? tasks : [];
                return mapProjectRowForManagement(proj, taskList);
            }),
        );
        return rows;
    },

    async getProjectBundle(projectId, currentUserId) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const [{ data: projectRes }, { data: tasks }] = await Promise.all([
            apiClient.get(ENDPOINTS.HIERARCHY.PROJECTS.DETAIL(pillar.taskForceId, pillar.id, projectId)),
            apiClient.get(ENDPOINTS.HIERARCHY.TASKS.LIST(pillar.taskForceId, pillar.id, projectId)),
        ]);
        const project = projectRes?.data ?? projectRes;
        if (!project) throw new Error('Project not found');
        const taskList = Array.isArray(tasks) ? tasks : [];
        const mappedTasks = taskList.map((t) => mapApiTaskToChairUi(t, currentUserId));
        return {
            pillar,
            project: {
                id: project.id,
                name: project.name,
                description: project.description,
                startDate: toYmd(project.projectStartDate),
                endDate: toYmd(project.projectEndDate),
                tasks: mappedTasks,
            },
        };
    },

    async createTask(projectId, { name, description, startDate, endDate, weight, targetValue, reportRange, isStar }) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const body = {
            name: String(name ?? '').trim(),
            description: String(description ?? '').trim(),
            startDate: startDate ? toIsoDateStart(startDate) : undefined,
            endDate: toIsoDateEnd(endDate),
            weight: normalizeInt1To100(weight, 'Weight', DEFAULT_WEIGHT),
            targetValue: normalizeInt1To100(
                targetValue,
                'Target value',
                DEFAULT_TARGET_VALUE,
            ),
            ...(isStar != null ? { isStar: Boolean(isStar) } : {}),
            ...(reportRange != null ? { reportRange: Math.trunc(Number(reportRange)) } : {}),
        };
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.TASKS.CREATE(pillar.taskForceId, pillar.id, projectId),
            body,
        );
        return data;
    },

    async updateTask(projectId, taskId, { name, description, startDate, endDate, weight, targetValue, reportRange, isStar }) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const patch = {};
        if (name !== undefined) patch.name = String(name ?? '').trim();
        if (description !== undefined) patch.description = String(description ?? '').trim();
        if (startDate !== undefined) patch.startDate = toIsoDateStart(startDate);
        if (endDate !== undefined) patch.endDate = toIsoDateEnd(endDate);
        if (weight !== undefined) patch.weight = normalizeInt1To100(weight, 'Weight');
        if (targetValue !== undefined) {
            patch.targetValue = normalizeInt1To100(targetValue, 'Target value');
        }
        if (isStar !== undefined) patch.isStar = Boolean(isStar);
        if (reportRange !== undefined) patch.reportRange = Math.trunc(Number(reportRange));
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.UPDATE(pillar.taskForceId, pillar.id, projectId, taskId),
            patch,
        );
        return data;
    },

    /* ── Task checkpoints (named stages on the progress track) ──
       Read is open to every governance reader; the writes below only succeed for
       a Project Manager / Admin scoped to the project (backend enforces it). */

    async listTaskCheckpoints(projectId, taskId) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINTS(pillar.taskForceId, pillar.id, projectId, taskId),
        );
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.checkpoints)) return data.checkpoints;
        if (Array.isArray(data?.data)) return data.data;
        return [];
    },

    async createTaskCheckpoint(projectId, taskId, payload) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINTS(pillar.taskForceId, pillar.id, projectId, taskId),
            toCheckpointPayload(payload),
        );
        return data;
    },

    async updateTaskCheckpoint(projectId, taskId, checkpointId, payload) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINT(
                pillar.taskForceId,
                pillar.id,
                projectId,
                taskId,
                checkpointId,
            ),
            toCheckpointPayload(payload),
        );
        return data;
    },

    async deleteTaskCheckpoint(projectId, taskId, checkpointId) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINT(
                pillar.taskForceId,
                pillar.id,
                projectId,
                taskId,
                checkpointId,
            ),
        );
        return { id: checkpointId };
    },

    async listAssignableUsers() {
        const { data } = await apiClient.get(ENDPOINTS.USERS.LIST, {
            params: { memberAssignableOnly: true },
        });
        const list = Array.isArray(data?.users) ? data.users : [];
        return list.map((u) => ({
            id: u.id,
            name: displayNameFromUser(u),
            email: u.email,
            role: primaryRoleLabel(u),
        }));
    },

    async assignUserToTask(projectId, taskId, { userId, isChair = false }) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.ASSIGN(pillar.taskForceId, pillar.id, projectId, taskId),
            { userId, isChair },
        );
        return data;
    },

    async removeTaskAssignee(projectId, taskId, userId) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.TASKS.REMOVE_ASSIGNEE(pillar.taskForceId, pillar.id, projectId, taskId, userId),
        );
        return { ok: true };
    },

    async sendTaskMessage(projectId, taskId, { senderId, message }) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.TASKS.MESSAGES(pillar.taskForceId, pillar.id, projectId, taskId),
            { senderId, message },
        );
        return data;
    },

    async verifyInProgress(projectId, taskId, inProgressId, { note } = {}) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        // The note is mandatory on both approve and reject (backend Joi min 5),
        // so it is always sent rather than omitted when blank.
        const body = { note: String(note ?? '').trim() };
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.INPROGRESS_VERIFY(
                pillar.taskForceId,
                pillar.id,
                projectId,
                taskId,
                inProgressId,
            ),
            body,
        );
        return data;
    },

    async rejectInProgress(projectId, taskId, inProgressId, { note } = {}) {
        const { pillar } = await this.listProjectsRaw();
        if (!pillar) throw new Error('No pillar assigned');
        // The note is mandatory on both approve and reject (backend Joi min 5),
        // so it is always sent rather than omitted when blank.
        const body = { note: String(note ?? '').trim() };
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.INPROGRESS_REJECT(
                pillar.taskForceId,
                pillar.id,
                projectId,
                taskId,
                inProgressId,
            ),
            body,
        );
        return data;
    },

    async pillarContext() {
        const body = await this.getMyPillar();
        const pillar = body?.data;
        if (!pillar?.taskForceId || !pillar?.id) {
            throw new Error('No pillar assigned to your account.');
        }
        return pillar;
    },

    async listProjects() {
        const pillar = await this.pillarContext();
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.PROJECTS.LIST(pillar.taskForceId, pillar.id),
        );
        const list = Array.isArray(data) ? data : [];
        return list.map(mapApiProjectToChairUi);
    },

    async listProjectVelocity() {
        const pillar = await this.pillarContext();
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.PROJECTS.VELOCITY(pillar.taskForceId, pillar.id),
        );
        return Array.isArray(data) ? data : [];
    },

    /** `params` optionally carries the dashboard date range (`startDate`/`endDate`). */
    async getPillarDashboardSummary(params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.PILLARS.MY_DASHBOARD_SUMMARY, { params });
        return data ?? null;
    },

    /** Cached AI briefing for the current user's pillar (`GET .../pillar-chair/pillars/my/ai-summary`). */
    async getPillarAiSummary() {
        const { data: body } = await apiClient.get(ENDPOINTS.PILLARS.MY_AI_SUMMARY);
        // API body is `{ data: summary | null }` — return the row only (same as unwrapping in the hook).
        return body?.data ?? null;
    },

    /** Body matches `createProjectSchema`: name, description, weight, targetValue; optional isStar/dates. */
    async createProject({ name, description, weight, targetValue, projectStartDate, projectEndDate, isStar }) {
        const pillar = await this.pillarContext();
        const body = {
            name: String(name ?? '').trim(),
            description: description != null ? String(description) : '',
            weight: weight != null ? Math.min(100, Math.max(1, Math.trunc(Number(weight)))) : 50,
            targetValue: Math.min(100, Math.max(1, Math.trunc(Number(targetValue) || 100))),
            ...(projectStartDate ? { projectStartDate: String(projectStartDate) } : {}),
            ...(projectEndDate ? { projectEndDate: String(projectEndDate) } : {}),
            ...(isStar != null ? { isStar: Boolean(isStar) } : {}),
        };
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.PROJECTS.CREATE(pillar.taskForceId, pillar.id),
            body,
        );
        return mapApiProjectToChairUi(data);
    },

    /** Partial body per `updateProjectSchema` (at least one field). */
    async updateProject(projectId, patch) {
        const pillar = await this.pillarContext();
        const body = {};
        if (patch.name !== undefined) body.name = String(patch.name ?? '').trim();
        if (patch.description !== undefined) body.description = String(patch.description ?? '');
        if (patch.weight !== undefined) body.weight = Math.min(100, Math.max(1, Math.trunc(Number(patch.weight) || 50)));
        if (patch.targetValue !== undefined) body.targetValue = Math.min(100, Math.max(1, Math.trunc(Number(patch.targetValue) || 100)));
        if (patch.projectStartDate !== undefined && patch.projectStartDate) body.projectStartDate = String(patch.projectStartDate);
        if (patch.projectEndDate !== undefined && patch.projectEndDate) body.projectEndDate = String(patch.projectEndDate);
        if (patch.isStar !== undefined) body.isStar = Boolean(patch.isStar);
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.PROJECTS.UPDATE(pillar.taskForceId, pillar.id, projectId),
            body,
        );
        return mapApiProjectToChairUi(data);
    },

    async deleteProject(projectId) {
        const pillar = await this.pillarContext();
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.PROJECTS.DELETE(pillar.taskForceId, pillar.id, projectId),
        );
        return { ok: true };
    },

    /** `assignProjectSchema`: { accountId } */
    async assignProjectManager(projectId, accountId) {
        const pillar = await this.pillarContext();
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.PROJECTS.ASSIGN(pillar.taskForceId, pillar.id, projectId),
            { accountId },
        );
        return mapApiProjectToChairUi(data);
    },

    // ── Project Locations ──────────────────────────────────────────────────

    async listProjectLocations({ cityId } = {}) {
        const pillar = await this.pillarContext();
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.LIST(pillar.taskForceId, pillar.id),
            { params: cityId ? { cityId: String(cityId) } : undefined },
        );
        return Array.isArray(data) ? data : [];
    },

    /** body: { cityId, name, description?, latitude?, longitude?, images? } */
    async createProjectLocation(body) {
        const pillar = await this.pillarContext();
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.CREATE(pillar.taskForceId, pillar.id),
            {
                cityId: String(body.cityId ?? '').trim(),
                name: String(body.name ?? '').trim(),
                description: body.description != null ? String(body.description) : undefined,
                latitude: body.latitude != null ? Number(body.latitude) : undefined,
                longitude: body.longitude != null ? Number(body.longitude) : undefined,
                images: Array.isArray(body.images) ? body.images : [],
            },
        );
        return data;
    },

    /** patch: { name?, description?, latitude?, longitude?, images? } */
    async updateProjectLocation(locationId, patch) {
        const pillar = await this.pillarContext();
        const body = {};
        if (patch.name !== undefined) body.name = String(patch.name ?? '').trim();
        if (patch.description !== undefined) body.description = String(patch.description ?? '');
        if (patch.latitude !== undefined) body.latitude = patch.latitude != null ? Number(patch.latitude) : null;
        if (patch.longitude !== undefined) body.longitude = patch.longitude != null ? Number(patch.longitude) : null;
        if (patch.images !== undefined) body.images = Array.isArray(patch.images) ? patch.images : [];
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.UPDATE(pillar.taskForceId, pillar.id, locationId),
            body,
        );
        return data;
    },

    async deleteProjectLocation(locationId) {
        const pillar = await this.pillarContext();
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.DELETE(pillar.taskForceId, pillar.id, locationId),
        );
        return { ok: true };
    },

    /** Upload images via the evidence endpoint and return MinIO object keys. */
    async uploadLocationImages(files) {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file);
        }
        const { data } = await apiClient.post(ENDPOINTS.STORAGE.EVIDENCE, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return Array.isArray(data?.objectKeys) ? data.objectKeys : [];
    },
};

export default pillarChairService;
